import type { CustomStoreImplementationOptions, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { defineStore } from 'pinia'
import type { WritableComputedRef } from 'vue'
import { NOOP, initAliveStoreIds, kapixContext, remove } from 'kapix-components-vue3'
import articleEnteteEnBrefComponentStoreCustom from './store.custom'

const storeName = 'articleEnteteEnBrefComponent'
const customImplement: CustomStoreImplementationOptions = articleEnteteEnBrefComponentStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function articleEnteteEnBrefComponentFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        $parentPage: NOOP,
        $parentComponent: NOOP,
        // Constants,
        $constants: {},
        // Data,
        $data: {
          articleTitle: undefined as Nullable<string>,
          subtitle: undefined as Nullable<string>,
          inShort: undefined as Nullable<string>,
          part1: undefined as Nullable<string>,
          part2: undefined as Nullable<string>,
          part3: undefined as Nullable<string>,
          part4: undefined as Nullable<string>,
          part5: undefined as Nullable<string>,
          part6: undefined as Nullable<string>
        },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      /* @ts-ignore: to allow override in your custom file */
      $articleTitle (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.articleTitle,
          set: (value) => {
            this.$data.articleTitle = value
            this.$emit('update:articleTitle', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $subtitle (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.subtitle,
          set: (value) => {
            this.$data.subtitle = value
            this.$emit('update:subtitle', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $inShort (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.inShort,
          set: (value) => {
            this.$data.inShort = value
            this.$emit('update:inShort', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $part1 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.part1,
          set: (value) => {
            this.$data.part1 = value
            this.$emit('update:part1', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $part2 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.part2,
          set: (value) => {
            this.$data.part2 = value
            this.$emit('update:part2', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $part3 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.part3,
          set: (value) => {
            this.$data.part3 = value
            this.$emit('update:part3', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $part4 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.part4,
          set: (value) => {
            this.$data.part4 = value
            this.$emit('update:part4', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $part5 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.part5,
          set: (value) => {
            this.$data.part5 = value
            this.$emit('update:part5', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $part6 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.part6,
          set: (value) => {
            this.$data.part6 = value
            this.$emit('update:part6', value)
          }
        })
      },
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return articleEnteteEnBrefComponentFactory(storeId)()
}

export const articleEnteteEnBrefComponentRaw = articleEnteteEnBrefComponentFactory()
export const articleEnteteEnBrefComponent = () => articleEnteteEnBrefComponentRaw() as MergeCustomStore<typeof articleEnteteEnBrefComponentStoreCustom.instance>
